'use client';

import Image from 'next/image';
import useModal from 'src/components/Modals/hooks/useModal';
import Link from 'src/components/primitives/Link/Link';
import Button from 'src/components/primitives/Button/Button';
import { cm } from 'src/utils/classNames';
import { WithClassName } from 'src/utils/UtilityTypes';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useReferralRewardStructure from 'src/hooks/useReferralRewardStructure';
import useAccountData from 'src/hooks/useAccountData';

interface Props extends WithClassName {
	imageClassName?: string;
	contentClassName?: string;
}

export default function NoReferralLinkCard({
	className,
	imageClassName,
	contentClassName,
}: Props) {
	const { show } = useModal();
	const isWalletConnected = useWalletIsConnected();
	const userAccountNotInitialized = useDriftAccountStore(
		(s) => s.userAccountNotInitialized
	);
	const { referrerEarnPercent } = useReferralRewardStructure();
	const accountIsDelegate = useAccountData()?.isDelegatedTo;

	const isDisabled = !isWalletConnected || accountIsDelegate;

	const showCreateReferralLinkModal = () => {
		if (isDisabled) {
			return;
		}

		show({ type: 'create_referral_link', props: {} });
	};

	const showDepositModal = () => {
		show({ type: 'deposit', props: {} });
	};

	return (
		<div
			className={cm(
				'flex flex-col gap-x-2xl gap-y-md sm:flex-row sm:items-center',
				'rounded-sm bg-container-bg p-md sm:p-lg',
				className
			)}
		>
			<div
				className={cm('relative h-50 w-full sm:h-40 sm:w-72', imageClassName)}
			>
				<Image
					src="/assets/images/referrals/referral-card-bg-light.svg"
					className="block dark:hidden"
					objectFit="cover"
					alt=""
					fill
				/>
				<Image
					src="/assets/images/referrals/referral-card-bg-dark.svg"
					className="hidden dark:block"
					objectFit="cover"
					alt=""
					fill
				/>
			</div>

			<div
				className={cm(
					'flex flex-col items-center justify-between gap-y-sm sm:flex-1 sm:flex-row',
					contentClassName
				)}
			>
				<div className="flex flex-col gap-y-sm sm:gap-y-lg">
					<h4 className="typo-label-sm text-text-default">
						Share your referral link and earn!
					</h4>
					<p className="flex flex-col gap-y-xs typo-body-xs text-text-secondary">
						<span>
							For every new trader that signs up with your referral link,
							you&apos;ll earn {referrerEarnPercent}% of their trading fees as a
							reward.
						</span>
						<Link
							href="https://docs.drift.trade/partnerships/referral-link"
							openInNewTab
						>
							Learn More
						</Link>
					</p>
				</div>

				<Button
					size="md"
					className="w-full sm:w-auto"
					variant="secondary"
					onClick={
						userAccountNotInitialized
							? showDepositModal
							: showCreateReferralLinkModal
					}
					disabled={isDisabled}
				>
					{userAccountNotInitialized ? 'Deposit' : 'Create my referral link'}
				</Button>
			</div>
		</div>
	);
}
